import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const MEAL_ICONS = { breakfast: 'Coffee', lunch: 'UtensilsCrossed', dinner: 'Moon' };
const MEAL_COLORS = { breakfast: 'amber', lunch: 'emerald', dinner: 'indigo' };

export default function MealTickets({ isAdmin }) {
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState(['breakfast', 'lunch', 'dinner']);
  const [validDate, setValidDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [filterMeal, setFilterMeal] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const printRef = useRef(null);

  useEffect(() => {
    fetchCardholders();
    fetchTickets();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [filterDate, filterMeal]);

  const fetchCardholders = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('restaurant_cardholders')?.select('id, full_name, category, building, department, accommodation_end, is_active')?.eq('is_active', true)?.order('full_name');
      if (!error) setCardholders(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      let query = supabase?.from('restaurant_meal_tickets')?.select('*, restaurant_cardholders(full_name, category, building)')?.eq('valid_date', filterDate)?.order('created_at', { ascending: false });
      if (filterMeal !== 'all') query = query?.eq('meal_type', filterMeal);
      const { data, error } = await query;
      if (!error) setTickets(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setTicketsLoading(false);
    }
  };

  const filteredCardholders = cardholders?.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return c?.full_name?.toLowerCase()?.includes(q) || c?.category?.toLowerCase()?.includes(q) || c?.building?.toLowerCase()?.includes(q);
  });

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const filtered = filteredCardholders?.map(c => c?.id);
    if (selectedIds?.length === filtered?.length) setSelectedIds([]);
    else setSelectedIds(filtered);
  };

  const toggleMeal = (meal) => {
    setSelectedMeals(prev => prev?.includes(meal) ? prev?.filter(m => m !== meal) : [...prev, meal]);
  };

  const generateTickets = async () => {
    if (!selectedIds?.length || !selectedMeals?.length) return;
    setGenerating(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const inserts = [];
      for (const chId of selectedIds) {
        for (const meal of selectedMeals) {
          inserts?.push({
            cardholder_id: chId,
            meal_type: meal,
            valid_date: validDate,
            status: 'active',
            generated_by: user?.id,
            ticket_number: '',
          });
        }
      }
      const { error } = await supabase?.from('restaurant_meal_tickets')?.insert(inserts);
      if (!error) {
        setSelectedIds([]);
        fetchTickets();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef?.current?.innerHTML;
    if (!printContent) return;
    const w = window.open('', '_blank');
    w?.document?.write(`<html><head><title>Meal Tickets</title>
      <style>
        body { font-family: 'DM Sans', sans-serif; padding: 20px; }
        .ticket { border: 2px solid #333; border-radius: 8px; padding: 16px; margin: 8px; display: inline-block; width: 200px; vertical-align: top; }
        .ticket-header { font-size: 11px; font-weight: bold; text-transform: uppercase; color: #666; }
        .ticket-name { font-size: 14px; font-weight: bold; margin: 4px 0; }
        .ticket-meal { font-size: 18px; font-weight: 900; text-transform: uppercase; }
        .ticket-date { font-size: 11px; color: #888; margin-top: 4px; }
        .ticket-num { font-size: 9px; color: #aaa; margin-top: 8px; border-top: 1px dashed #ccc; padding-top: 4px; }
        @media print { body { padding: 0; } }
      </style></head><body>${printContent}</body></html>`);
    w?.document?.close();
    w?.print();
  };

  const statusBadge = (status) => {
    const map = { active: 'bg-emerald-100 text-emerald-700', used: 'bg-slate-100 text-slate-600', expired: 'bg-red-100 text-red-600', cancelled: 'bg-orange-100 text-orange-600' };
    return map?.[status] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-6">
      {isAdmin && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Ticket" size={16} className="text-primary" />
            Generate Meal Tickets
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Valid Date</label>
              <input type="date" value={validDate} onChange={e => setValidDate(e?.target?.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Meal Types</label>
              <div className="flex gap-2">
                {MEAL_TYPES?.map(m => (
                  <button key={m} onClick={() => toggleMeal(m)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${selectedMeals?.includes(m) ? `bg-${MEAL_COLORS?.[m]}-100 border-${MEAL_COLORS?.[m]}-300 text-${MEAL_COLORS?.[m]}-700` : 'bg-muted border-border text-muted-foreground'}`}>
                    {m?.charAt(0)?.toUpperCase() + m?.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-end">
              <button onClick={generateTickets} disabled={generating || !selectedIds?.length || !selectedMeals?.length}
                className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all hover:opacity-90">
                {generating ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Zap" size={14} />}
                Generate {selectedIds?.length > 0 ? `(${selectedIds?.length})` : ''}
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-muted-foreground">Select Residents / Students / Staff / Visitors</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e?.target?.value)}
                  className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-40" />
                <button onClick={toggleSelectAll} className="text-xs text-primary font-medium hover:underline">
                  {selectedIds?.length === filteredCardholders?.length && filteredCardholders?.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8"><Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" /></div>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {filteredCardholders?.map(c => (
                  <label key={c?.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer">
                    <input type="checkbox" checked={selectedIds?.includes(c?.id)} onChange={() => toggleSelect(c?.id)}
                      className="rounded border-border text-primary focus:ring-primary/30" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{c?.full_name}</div>
                      <div className="text-xs text-muted-foreground">{c?.category} {c?.building ? `· ${c?.building}` : ''}</div>
                    </div>
                    {c?.accommodation_end && new Date(c.accommodation_end) < new Date() && (
                      <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Expired</span>
                    )}
                  </label>
                ))}
                {filteredCardholders?.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">No cardholders found</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Tickets List */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="TicketCheck" size={16} className="text-primary" />
            Meal Tickets
          </h3>
          <div className="flex flex-wrap gap-2">
            <input type="date" value={filterDate} onChange={e => setFilterDate(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <select value={filterMeal} onChange={e => setFilterMeal(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">All Meals</option>
              {MEAL_TYPES?.map(m => <option key={m} value={m}>{m?.charAt(0)?.toUpperCase() + m?.slice(1)}</option>)}
            </select>
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-muted transition-all">
              <Icon name="Printer" size={13} />Print
            </button>
          </div>
        </div>

        {ticketsLoading ? (
          <div className="flex items-center justify-center py-10"><Icon name="Loader2" size={22} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div ref={printRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {tickets?.map(t => (
                <div key={t?.id} className={`border-2 rounded-xl p-3 space-y-1 ${t?.status === 'used' ? 'opacity-60 border-slate-200' : t?.status === 'expired' ? 'border-red-200' : 'border-primary/30'}`}>
                  <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide">{t?.restaurant_cardholders?.category}</div>
                  <div className="text-sm font-bold text-foreground leading-tight">{t?.restaurant_cardholders?.full_name}</div>
                  <div className={`text-base font-black uppercase text-${MEAL_COLORS?.[t?.meal_type]}-600`}>{t?.meal_type}</div>
                  <div className="text-xs text-muted-foreground">{t?.valid_date}</div>
                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded font-medium ${statusBadge(t?.status)}`}>{t?.status}</span>
                  <div className="text-xs text-muted-foreground/60 border-t border-dashed border-border pt-1 mt-1 truncate">{t?.ticket_number}</div>
                </div>
              ))}
            </div>
            {tickets?.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">No tickets found for this date</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
